import { useCallback, useMemo, useState } from 'react';
import { Badge, Card, Input, Button, LoadingSkeleton, Tabs, Drawer } from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  useTrialBalance,
  useIncomeStatement,
  useBalanceSheet,
  useChartOfAccounts,
  useJournalEntries,
  useAccountingCtx,
  type StatementLine,
} from './useAccounting';

export type TabKey = 'trial-balance' | 'income-statement' | 'balance-sheet';

export const STATEMENTS_TABS = [
  { key: 'trial-balance' as TabKey, label: 'Balancete' },
  { key: 'income-statement' as TabKey, label: 'DRE' },
  { key: 'balance-sheet' as TabKey, label: 'Balanço Patrimonial' },
];

/* ── Apresentação de valores ──────────────────────────────────────────────
   Demonstrativo contábil escreve valor negativo entre parênteses, não com
   sinal de menos: é a convenção que o contador lê. */

function formatAccounting(value: number): string {
  if (value < 0) return `(${formatCurrency(Math.abs(value))})`;
  return formatCurrency(value);
}

/** Linha de dedução/despesa: o saldo chega positivo na natureza da conta e
    entra no demonstrativo reduzindo o resultado. */
function formatDeduction(value: number): string {
  return value === 0 ? formatCurrency(0) : `(${formatCurrency(Math.abs(value))})`;
}

/* ── Agrupamento por conta sintética ──────────────────────────────────────
   O servidor devolve só contas analíticas (`4.01.001`). A conta sintética que
   as agrupa (`4.01`) está no plano de contas; o código é o que liga as duas.
   O total do grupo é a soma das analíticas — nada aqui é inventado. */

interface StatementGroup {
  code: string;
  name: string;
  total: number;
  lines: StatementLine[];
}

function parentCode(code: string): string {
  const parts = code.split('.');
  return parts.length > 1 ? parts.slice(0, -1).join('.') : code;
}

function groupByParent(
  lines: StatementLine[],
  nameOf: (code: string) => string | null,
): StatementGroup[] {
  const groups = new Map<string, StatementGroup>();
  for (const line of lines) {
    const code = parentCode(line.accountCode);
    const group = groups.get(code);
    if (group) {
      group.total += line.balance;
      group.lines.push(line);
    } else {
      groups.set(code, {
        code,
        name: nameOf(code) ?? code,
        total: line.balance,
        lines: [line],
      });
    }
  }
  return Array.from(groups.values()).sort((a, b) => a.code.localeCompare(b.code));
}

/* ── Estados de carga e erro ── */

function StatementLoading({ label }: { label: string }): JSX.Element {
  return (
    <div className="stmt-state">
      <LoadingSkeleton />
      <span>{label}</span>
    </div>
  );
}

function StatementError({ label, error }: { label: string; error: unknown }): JSX.Element {
  return (
    <div className="stmt-state stmt-state--error">
      <p className="stmt-state__title">{label}</p>
      <p className="stmt-state__detail">
        {error instanceof Error ? error.message : 'Erro desconhecido'}
      </p>
    </div>
  );
}

function StatementEmpty({ label }: { label: string }): JSX.Element {
  return (
    <div className="stmt-state stmt-state--empty">
      <p>{label}</p>
    </div>
  );
}

/* ── Painel de origem da linha ────────────────────────────────────────────
   A referência chama de "Audit View": a partir de uma linha do demonstrativo,
   os lançamentos que a produziram. `GET /entries?accountId=` já filtra por
   conta no servidor (`lines: { some: { accountId } }`), então a lista é real.
   O que ela NÃO traz é o "Reconciled with General Ledger" do protótipo: não
   existe marca de conciliação por linha de demonstrativo no backend. */

interface SourcePanelProps {
  line: { accountCode: string; accountName: string } | null;
  accountId: string | null;
  startDate?: string;
  endDate?: string;
  onClose: () => void;
}

function SourceEntriesDrawer({
  line,
  accountId,
  startDate,
  endDate,
  onClose,
}: SourcePanelProps): JSX.Element {
  const ctx = useAccountingCtx();
  const { data, isLoading, error } = useJournalEntries(
    line && accountId ? ctx : null,
    { limit: 50, accountId: accountId ?? undefined, startDate, endDate },
  );

  const entries = data?.data ?? [];

  return (
    <Drawer
      open={!!line}
      onClose={onClose}
      title={line ? line.accountName : 'Origem'}
      description={line ? `Conta ${line.accountCode} · lançamentos do período` : undefined}
      size="wide"
    >
      {!accountId && (
        <p className="stmt-drawer__note">
          Conta não encontrada no plano de contas — sem origem para listar.
        </p>
      )}

      {accountId && isLoading && <StatementLoading label="Carregando lançamentos..." />}

      {accountId && error && !isLoading && (
        <StatementError label="Erro ao carregar lançamentos" error={error} />
      )}

      {accountId && !isLoading && !error && entries.length === 0 && (
        <p className="stmt-drawer__note">Nenhum lançamento nesta conta no período.</p>
      )}

      {accountId && !isLoading && !error && entries.length > 0 && (
        <ul className="stmt-source">
          {entries.map((entry) => (
            <li key={entry.id} className="stmt-source__item">
              <div className="stmt-source__top">
                <span className="stmt-source__number">{entry.entryNumber}</span>
                <span className="stmt-source__value">{formatCurrency(entry.totalDebit)}</span>
              </div>
              <p className="stmt-source__desc">{entry.description}</p>
              <div className="stmt-source__meta">
                <span>{formatDate(entry.date)}</span>
                <span className="stmt-source__origin">{entry.source}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}

/* ── Balancete de Verificação ── */

const ACCOUNT_TYPE_LABEL: Record<string, string> = {
  ASSET: 'Ativo',
  LIABILITY: 'Passivo',
  EQUITY: 'Patrimônio Líquido',
  REVENUE: 'Receita',
  EXPENSE: 'Despesa',
};

export function TrialBalanceTab(): JSX.Element {
  const today = new Date().toISOString().split('T')[0] ?? '';
  const [date, setDate] = useState(today);

  const ctx = useAccountingCtx();
  const { data, isLoading, error, refetch } = useTrialBalance(ctx, date || undefined);

  const rows = data?.accounts ?? [];
  /* Balancete fecha quando o débito acumulado iguala o crédito acumulado.
     Os dois totais vêm da soma das contas listadas — a listagem é completa. */
  const difference = +((data?.totalDebit ?? 0) - (data?.totalCredit ?? 0)).toFixed(2);

  return (
    <div>
      <Card padding="1rem" variant="outlined" className="stmt-filters">
        <div className="stmt-filters__row">
          <div className="stmt-filters__field">
            <Input label="Data" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>Atualizar</Button>
        </div>
      </Card>

      <Card variant="outlined" padding="0">
        <div className="stmt-card__head">
          <h3 className="stmt-card__title">Balancete de Verificação</h3>
          <span className="stmt-card__count">
            {rows.length} {rows.length === 1 ? 'conta' : 'contas'}
          </span>
        </div>

        {isLoading && <StatementLoading label="Carregando balancete..." />}
        {error && !isLoading && <StatementError label="Erro ao carregar balancete" error={error} />}
        {!isLoading && !error && rows.length === 0 && (
          <StatementEmpty label="Nenhuma conta com movimento até a data selecionada." />
        )}

        {!isLoading && !error && rows.length > 0 && (
          <div className="stmt-scroll">
            <table className="stmt-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Conta</th>
                  <th>Tipo</th>
                  <th className="stmt-center">Nat.</th>
                  <th className="stmt-right">Débito</th>
                  <th className="stmt-right">Crédito</th>
                  <th className="stmt-right">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.code} className="stmt-row">
                    <td className="stmt-mono">{row.code}</td>
                    <td>{row.name}</td>
                    <td className="stmt-muted">{ACCOUNT_TYPE_LABEL[row.type] ?? row.type}</td>
                    <td className="stmt-center">
                      <span className={row.nature === 'DEBIT' ? 'stmt-dc stmt-dc--d' : 'stmt-dc stmt-dc--c'}>
                        {row.nature === 'DEBIT' ? 'D' : 'C'}
                      </span>
                    </td>
                    <td className="stmt-num">{row.debit > 0 ? formatCurrency(row.debit) : '—'}</td>
                    <td className="stmt-num">{row.credit > 0 ? formatCurrency(row.credit) : '—'}</td>
                    <td className="stmt-num stmt-strong">{formatAccounting(row.balance)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="stmt-total">
                  <td colSpan={4}>Totais</td>
                  <td className="stmt-num">{formatCurrency(data?.totalDebit ?? 0)}</td>
                  <td className="stmt-num">{formatCurrency(data?.totalCredit ?? 0)}</td>
                  <td className="stmt-num" />
                </tr>
                <tr className={difference === 0 ? 'stmt-check stmt-check--ok' : 'stmt-check stmt-check--bad'}>
                  <td colSpan={4}>Diferença</td>
                  <td className="stmt-num" colSpan={2}>{formatCurrency(difference)}</td>
                  <td className="stmt-center">
                    <span className="material-symbols-outlined stmt-check__icon" aria-hidden="true">
                      {difference === 0 ? 'check_circle' : 'error'}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ── DRE ── */

type DreView = 'actual' | 'previous-year';

function shiftYear(isoDate: string, years: number): string {
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().split('T')[0] ?? isoDate;
}

function variationPercent(current: number, previous: number): string {
  if (previous === 0) return '—';
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

export function IncomeStatementTab(): JSX.Element {
  const today = new Date();
  const firstOfYear = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0] ?? '';
  const todayStr = today.toISOString().split('T')[0] ?? '';

  const [startDate, setStartDate] = useState(firstOfYear);
  const [endDate, setEndDate] = useState(todayStr);
  const [view, setView] = useState<DreView>('actual');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<StatementLine | null>(null);

  const ctx = useAccountingCtx();
  const { data, isLoading, error, refetch } = useIncomeStatement(
    ctx,
    startDate || undefined,
    endDate || undefined,
  );

  /* "vs Ano anterior" é o MESMO endpoint com as datas recuadas em um ano —
     dado real, não projeção. O protótipo também oferecia "vs Budget"; não há
     orçamento em lugar nenhum do backend, então essa opção não existe aqui. */
  const comparing = view === 'previous-year';
  const previous = useIncomeStatement(
    comparing ? ctx : null,
    startDate ? shiftYear(startDate, -1) : undefined,
    endDate ? shiftYear(endDate, -1) : undefined,
  );

  const { data: chart } = useChartOfAccounts(ctx);
  const accounts = useMemo(() => {
    const flat: { id: string; code: string; name: string }[] = [];
    const visit = (nodes: typeof chart): void => {
      for (const node of nodes ?? []) {
        flat.push({ id: node.id, code: node.code, name: node.name });
        if (node.children) visit(node.children);
      }
    };
    visit(chart);
    return flat;
  }, [chart]);

  const nameOf = useCallback(
    (code: string): string | null => accounts.find((a) => a.code === code)?.name ?? null,
    [accounts],
  );
  const idOf = (code: string): string | null =>
    accounts.find((a) => a.code === code)?.id ?? null;

  const revenueGroups = useMemo(
    () => groupByParent(data?.revenue ?? [], nameOf),
    [data?.revenue, nameOf],
  );
  const expenseGroups = useMemo(
    () => groupByParent(data?.expenses ?? [], nameOf),
    [data?.expenses, nameOf],
  );

  const previousByCode = useMemo(() => {
    const map = new Map<string, number>();
    for (const line of [...(previous.data?.revenue ?? []), ...(previous.data?.expenses ?? [])]) {
      map.set(line.accountCode, line.balance);
    }
    return map;
  }, [previous.data]);

  const previousGroupTotal = (group: StatementGroup): number =>
    group.lines.reduce((sum, l) => sum + (previousByCode.get(l.accountCode) ?? 0), 0);

  const toggle = (code: string): void => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const columns = comparing ? 5 : 3;

  const renderGroup = (group: StatementGroup, deduction: boolean): JSX.Element[] => {
    const open = expanded.has(group.code);
    const prev = previousGroupTotal(group);
    const rows: JSX.Element[] = [
      <tr key={group.code} className="stmt-group" onClick={() => toggle(group.code)}>
        <td className="stmt-mono">{group.code}</td>
        <td>
          <span className="material-symbols-outlined stmt-group__chevron" aria-hidden="true">
            {open ? 'expand_more' : 'chevron_right'}
          </span>
          {group.name}
        </td>
        <td className="stmt-num">
          {deduction ? formatDeduction(group.total) : formatAccounting(group.total)}
        </td>
        {comparing && (
          <>
            <td className="stmt-num">
              {deduction ? formatDeduction(prev) : formatAccounting(prev)}
            </td>
            <td className="stmt-num stmt-muted">{variationPercent(group.total, prev)}</td>
          </>
        )}
      </tr>,
    ];

    if (open) {
      for (const line of group.lines) {
        const linePrev = previousByCode.get(line.accountCode) ?? 0;
        rows.push(
          <tr
            key={line.accountCode}
            className="stmt-row stmt-row--child"
            onClick={() => setSelected(line)}
          >
            <td className="stmt-mono">{line.accountCode}</td>
            <td className="stmt-indent">{line.accountName}</td>
            <td className="stmt-num">
              {deduction ? formatDeduction(line.balance) : formatAccounting(line.balance)}
            </td>
            {comparing && (
              <>
                <td className="stmt-num">
                  {deduction ? formatDeduction(linePrev) : formatAccounting(linePrev)}
                </td>
                <td className="stmt-num stmt-muted">{variationPercent(line.balance, linePrev)}</td>
              </>
            )}
          </tr>,
        );
      }
    }

    return rows;
  };

  const totalRow = (
    label: string,
    value: number,
    prev: number,
    emphasis: boolean,
  ): JSX.Element => (
    <tr className={emphasis ? 'stmt-total stmt-total--result' : 'stmt-total'}>
      <td />
      <td>{label}</td>
      <td className="stmt-num">{formatAccounting(value)}</td>
      {comparing && (
        <>
          <td className="stmt-num">{formatAccounting(prev)}</td>
          <td className="stmt-num stmt-muted">{variationPercent(value, prev)}</td>
        </>
      )}
    </tr>
  );

  return (
    <div>
      <Card padding="1rem" variant="outlined" className="stmt-filters">
        <div className="stmt-filters__row">
          <div className="stmt-filters__field">
            <Input label="Data início" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="stmt-filters__field">
            <Input label="Data fim" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>

          <div className="stmt-toggle" role="group" aria-label="Base de comparação">
            <button
              type="button"
              className={view === 'actual' ? 'stmt-toggle__btn is-active' : 'stmt-toggle__btn'}
              onClick={() => setView('actual')}
            >
              Realizado
            </button>
            <button
              type="button"
              className={comparing ? 'stmt-toggle__btn is-active' : 'stmt-toggle__btn'}
              onClick={() => setView('previous-year')}
            >
              vs Ano anterior
            </button>
          </div>

          <Button variant="secondary" size="sm" onClick={() => refetch()}>Atualizar</Button>
        </div>
      </Card>

      {isLoading && <StatementLoading label="Carregando DRE..." />}
      {error && !isLoading && <StatementError label="Erro ao carregar DRE" error={error} />}

      {!isLoading && !error && data && (
        <Card variant="outlined" padding="0">
          <div className="stmt-card__head">
            <div>
              <h3 className="stmt-card__title">Demonstração do Resultado do Exercício</h3>
              <p className="stmt-card__period">
                Período: {formatDate(startDate)} a {formatDate(endDate)}
                {comparing ? ` · comparado a ${formatDate(shiftYear(startDate, -1))} a ${formatDate(shiftYear(endDate, -1))}` : ''}
              </p>
            </div>
            <Badge variant={data.netIncome >= 0 ? 'success' : 'error'} size="sm">
              {data.netIncome >= 0 ? 'Lucro no período' : 'Prejuízo no período'}
            </Badge>
          </div>

          {data.revenue.length === 0 && data.expenses.length === 0 ? (
            <StatementEmpty label="Nenhum lançamento em conta de resultado no período selecionado." />
          ) : (
            <div className="stmt-scroll">
              <table className="stmt-table stmt-table--dre">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Descrição</th>
                    <th className="stmt-right">Realizado (R$)</th>
                    {comparing && <th className="stmt-right">Ano anterior (R$)</th>}
                    {comparing && <th className="stmt-right">Variação</th>}
                  </tr>
                </thead>
                <tbody>
                  <tr className="stmt-section">
                    <td colSpan={columns}>Receitas</td>
                  </tr>
                  {revenueGroups.flatMap((g) => renderGroup(g, false))}
                  {totalRow(
                    'Total de Receitas',
                    data.totalRevenue,
                    previous.data?.totalRevenue ?? 0,
                    false,
                  )}

                  <tr className="stmt-section">
                    <td colSpan={columns}>Despesas</td>
                  </tr>
                  {expenseGroups.flatMap((g) => renderGroup(g, true))}
                  {totalRow(
                    '(-) Total de Despesas',
                    -data.totalExpenses,
                    -(previous.data?.totalExpenses ?? 0),
                    false,
                  )}
                </tbody>
                <tfoot>
                  {totalRow(
                    'Resultado do Período',
                    data.netIncome,
                    previous.data?.netIncome ?? 0,
                    true,
                  )}
                </tfoot>
              </table>
            </div>
          )}
        </Card>
      )}

      <SourceEntriesDrawer
        line={selected}
        accountId={selected ? idOf(selected.accountCode) : null}
        startDate={startDate || undefined}
        endDate={endDate || undefined}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

/* ── Balanço Patrimonial ── */

function BalanceGroup({
  title, lines, total, tone,
}: {
  title: string;
  lines: StatementLine[];
  total: number;
  tone: 'asset' | 'liability' | 'equity';
}): JSX.Element {
  return (
    <Card variant="outlined" padding="0">
      <div className={`stmt-bs__head stmt-bs__head--${tone}`}>
        <h3 className="stmt-bs__title">{title}</h3>
        <span className="stmt-bs__total">{formatAccounting(total)}</span>
      </div>
      {lines.length === 0 ? (
        <p className="stmt-bs__empty">Nenhuma conta com saldo.</p>
      ) : (
        <table className="stmt-table stmt-table--compact">
          <tbody>
            {lines.map((line) => (
              <tr key={line.accountCode} className="stmt-row">
                <td className="stmt-mono">{line.accountCode}</td>
                <td>{line.accountName}</td>
                <td className="stmt-num">{formatAccounting(line.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

export function BalanceSheetTab(): JSX.Element {
  const today = new Date().toISOString().split('T')[0] ?? '';
  const [date, setDate] = useState(today);

  const ctx = useAccountingCtx();
  const { data, isLoading, error, refetch } = useBalanceSheet(ctx, date || undefined);

  return (
    <div>
      <Card padding="1rem" variant="outlined" className="stmt-filters">
        <div className="stmt-filters__row">
          <div className="stmt-filters__field">
            <Input label="Data" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>Atualizar</Button>
        </div>
      </Card>

      {isLoading && <StatementLoading label="Carregando balanço patrimonial..." />}
      {error && !isLoading && (
        <StatementError label="Erro ao carregar balanço patrimonial" error={error} />
      )}

      {!isLoading && !error && data && (
        <>
          {/* A conferência da equação é do servidor (`isBalanced`), não uma
              comparação refeita aqui: quem fecha o balanço é quem o monta. */}
          <Card variant="elevated" padding="1.25rem" className="stmt-equation">
            <div className="stmt-equation__row">
              <div className="stmt-equation__term">
                <span className="stmt-equation__label">Ativo</span>
                <span className="stmt-equation__value stmt-equation__value--asset">
                  {formatAccounting(data.totalAssets)}
                </span>
              </div>
              <span className="stmt-equation__op">=</span>
              <div className="stmt-equation__term">
                <span className="stmt-equation__label">Passivo</span>
                <span className="stmt-equation__value stmt-equation__value--liability">
                  {formatAccounting(data.totalLiabilities)}
                </span>
              </div>
              <span className="stmt-equation__op">+</span>
              <div className="stmt-equation__term">
                <span className="stmt-equation__label">Patrimônio Líquido</span>
                <span className="stmt-equation__value stmt-equation__value--equity">
                  {formatAccounting(data.totalEquity)}
                </span>
              </div>
              <Badge variant={data.isBalanced ? 'success' : 'error'} size="sm">
                {data.isBalanced ? 'Balanço fecha' : 'Balanço não fecha'}
              </Badge>
            </div>
          </Card>

          <div className="stmt-bs__grid">
            <BalanceGroup title="Ativo" lines={data.assets} total={data.totalAssets} tone="asset" />
            <BalanceGroup title="Passivo" lines={data.liabilities} total={data.totalLiabilities} tone="liability" />
            <BalanceGroup title="Patrimônio Líquido" lines={data.equity} total={data.totalEquity} tone="equity" />
          </div>
        </>
      )}
    </div>
  );
}

/* ── Page ── */

export function FinancialStatementsPage(): JSX.Element {
  const [activeTab, setActiveTab] = useState<TabKey>('income-statement');

  return (
    <div className="stmt-page">
      <div className="mb-lg">
        <h1 className="font-display-lg text-display-lg text-on-surface m-0">
          Demonstrações Financeiras
        </h1>
        <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
          Balancete de verificação, DRE e balanço patrimonial, a partir dos lançamentos contábeis.
        </p>
      </div>

      <Tabs
        tabs={STATEMENTS_TABS}
        value={activeTab}
        onChange={(key) => setActiveTab(key as TabKey)}
        variant="underline"
        aria-label="Abas de demonstrativos financeiros"
      />

      <div className="stmt-body">
        {activeTab === 'trial-balance' && <TrialBalanceTab />}
        {activeTab === 'income-statement' && <IncomeStatementTab />}
        {activeTab === 'balance-sheet' && <BalanceSheetTab />}
      </div>

      <style>{`
        .stmt-page { padding: 1.5rem; }
        .stmt-body { margin-top: 1rem; }

        .stmt-filters { margin-bottom: 1rem; }
        .stmt-filters__row { display: flex; align-items: flex-end; gap: 1rem; flex-wrap: wrap; }
        .stmt-filters__field { flex: 0 0 180px; }

        .stmt-toggle { display: inline-flex; border: 1px solid #c4c6cf; border-radius: 2px; overflow: hidden; }
        .stmt-toggle__btn { padding: 0.4375rem 0.875rem; background: #ffffff; border: 0; border-right: 1px solid #c4c6cf; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; letter-spacing: 0.02em; color: #43474e; cursor: pointer; }
        .stmt-toggle__btn:last-child { border-right: 0; }
        .stmt-toggle__btn.is-active { background: #002045; color: #ffffff; }

        .stmt-card__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; padding: 1rem 1rem 0.75rem; border-bottom: 1px solid #eceef0; }
        .stmt-card__title { margin: 0; font-size: 1.25rem; font-weight: 600; color: #191c1e; }
        .stmt-card__period { margin: 0.25rem 0 0; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; }
        .stmt-card__count { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; white-space: nowrap; }

        .stmt-scroll { overflow-x: auto; }
        .stmt-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .stmt-table th { position: sticky; top: 0; text-align: left; padding: 0.5rem 0.75rem; background: #f2f4f6; color: #43474e; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; border-bottom: 1px solid #c4c6cf; white-space: nowrap; }
        .stmt-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .stmt-table--compact td { padding: 0.375rem 0.75rem; font-size: 0.8125rem; }

        .stmt-right { text-align: right; }
        .stmt-center { text-align: center; }
        .stmt-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .stmt-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; white-space: nowrap; }
        .stmt-muted { color: #43474e; }
        .stmt-strong { font-weight: 600; }
        .stmt-indent { padding-left: 2.25rem !important; }

        .stmt-row:hover td { background: #f2f4f6; }
        .stmt-row--child { cursor: pointer; }
        .stmt-row--child td { color: #43474e; }

        .stmt-section td { padding: 0.5rem 0.75rem; background: #f7f9fb; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; color: #43474e; border-bottom: 1px solid #c4c6cf; }

        .stmt-group { cursor: pointer; font-weight: 600; }
        .stmt-group:hover td { background: #f2f4f6; }
        .stmt-group__chevron { font-size: 18px; vertical-align: -4px; color: #43474e; margin-right: 0.25rem; }

        .stmt-total td { background: #f7f9fb; font-weight: 600; border-top: 1px solid #c4c6cf; }
        .stmt-total--result td { background: #eceef0; font-size: 0.9375rem; border-top: 2px solid #191c1e; }

        .stmt-check td { font-weight: 600; }
        .stmt-check--ok td { color: #1b7f4d; }
        .stmt-check--bad td { color: #ba1a1a; }
        .stmt-check__icon { font-size: 16px; }

        .stmt-dc { display: inline-block; min-width: 20px; padding: 0 4px; border-radius: 2px; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; }
        .stmt-dc--d { background: #d6e3ff; color: #001b3c; }
        .stmt-dc--c { background: #e0e3e5; color: #191c1e; }

        .stmt-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.5rem; padding: 3rem 2rem; color: #43474e; font-size: 0.875rem; }
        .stmt-state--error { color: #ba1a1a; }
        .stmt-state--empty { color: #74777f; }
        .stmt-state__title { margin: 0; font-weight: 600; }
        .stmt-state__detail { margin: 0; font-size: 0.875rem; color: #74777f; }

        .stmt-equation { margin-bottom: 1rem; }
        .stmt-equation__row { display: flex; align-items: center; justify-content: center; gap: 1.5rem; flex-wrap: wrap; }
        .stmt-equation__term { text-align: center; }
        .stmt-equation__label { display: block; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; color: #43474e; }
        .stmt-equation__value { display: block; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 1.0625rem; font-weight: 600; }
        .stmt-equation__value--asset { color: #002045; }
        .stmt-equation__value--liability { color: #7a3f00; }
        .stmt-equation__value--equity { color: #1b7f4d; }
        .stmt-equation__op { font-size: 1.125rem; color: #74777f; }

        .stmt-bs__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1rem; align-items: start; }
        .stmt-bs__head { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.75rem 1rem; border-bottom: 1px solid #c4c6cf; }
        .stmt-bs__head--asset { border-left: 3px solid #002045; }
        .stmt-bs__head--liability { border-left: 3px solid #7a3f00; }
        .stmt-bs__head--equity { border-left: 3px solid #1b7f4d; }
        .stmt-bs__title { margin: 0; font-size: 1rem; font-weight: 600; color: #191c1e; }
        .stmt-bs__total { font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; font-weight: 600; color: #191c1e; }
        .stmt-bs__empty { margin: 0; padding: 1.5rem 1rem; text-align: center; color: #74777f; font-size: 0.875rem; }

        .stmt-drawer__note { margin: 0; padding: 1.5rem 0; text-align: center; color: #74777f; font-size: 0.875rem; }
        .stmt-source { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
        .stmt-source__item { border: 1px solid #c4c6cf; border-radius: 4px; padding: 0.625rem 0.75rem; background: #ffffff; }
        .stmt-source__top { display: flex; align-items: baseline; justify-content: space-between; gap: 0.75rem; }
        .stmt-source__number { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; color: #002045; }
        .stmt-source__value { font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; font-weight: 600; }
        .stmt-source__desc { margin: 0.25rem 0; font-size: 0.875rem; color: #191c1e; }
        .stmt-source__meta { display: flex; align-items: center; justify-content: space-between; font-size: 0.75rem; color: #43474e; }
        .stmt-source__origin { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; background: #e0e3e5; border-radius: 2px; padding: 1px 5px; }
      `}</style>
    </div>
  );
}
